import { useState } from "react";
import { Article, SpecialArticle } from "@/types/article";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RegularArticlesTable } from "./admin/RegularArticlesTable";
import { SpecialArticlesTable } from "./admin/SpecialArticlesTable";

interface AdminArticleListProps {
  articles: Article[] | undefined;
  specialArticles: SpecialArticle[] | undefined;
  isLoading: boolean;
  onDelete: (id: string, isSpecial?: boolean) => void;
}

const AdminArticleList = ({
  articles,
  specialArticles,
  isLoading,
  onDelete,
}: AdminArticleListProps) => {
  const { toast } = useToast();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string, isSpecial: boolean = false) => {
    try {
      setDeletingId(id);
      const table = isSpecial ? "special_articles" : "articles";
      await supabase.from(table).delete().eq("id", id);
      onDelete(id, isSpecial);
      toast({
        title: "記事を削除しました",
        description: "記事の削除が完了しました。",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "エラーが発生しました",
        description: "記事の削除に失敗しました。",
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <div className="p-4">読み込み中...</div>;
  }

  return (
    <Tabs defaultValue="regular" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="regular">通常記事</TabsTrigger>
        <TabsTrigger value="special">スペシャル記事</TabsTrigger>
      </TabsList>

      <TabsContent value="regular">
        <RegularArticlesTable
          articles={articles}
          onDelete={handleDelete}
          deletingId={deletingId}
        />
      </TabsContent>

      <TabsContent value="special">
        <SpecialArticlesTable
          articles={specialArticles}
          onDelete={handleDelete}
          deletingId={deletingId}
        />
      </TabsContent>
    </Tabs>
  );
};

export default AdminArticleList;
